import { cache } from 'react'
import {
  QueryClient,
  QueryClientConfig,
  QueryCache,
  MutationCache,
} from '@tanstack/react-query'
import { getTranslation } from './i18n'
import { toast } from 'react-toastify'
import calculateRetryDelay from 'utils/calculateRetryDelay'
import {
  classifyError,
  isServerError,
  isNetworkError,
  RETRYABLE_STATUS_CODES,
  getLogger,
} from 'tokyu-hooks'

const onError = async (error: unknown) => {
  const appError = classifyError(error)

  if (isServerError(appError) && appError.status === 503) {
    window.location.href = '/maintenance?source=tripla-503'
    return
  }

  // 401 errors are handled by useAuthentication (token refresh + logout).
  // A hard redirect here would race with in-progress navigations

  if (isServerError(appError) && appError.status === 502) {
    const language = window?.location?.pathname
      ?.match(/\/([a-zA-Z-_]*)/g)?.[0]
      ?.replace('/', '')

    const { t } = await getTranslation(language || 'ja')

    toast.error(`502: ${t('There are something wrong')}`)
  }

  if (isNetworkError(appError)) {
    getLogger().warn('Query client network error', {
      error,
      appErrorKind: appError.kind,
    })
    return
  }

  getLogger().error('Query client error', error, {
    appErrorKind: appError.kind,
  })
}

const retryFunction = (failureCount: number, error: unknown): boolean => {
  const appError = classifyError(error)
  if (
    isServerError(appError) &&
    (RETRYABLE_STATUS_CODES as readonly number[]).includes(appError.status)
  ) {
    return failureCount < 3
  }
  if (isNetworkError(appError)) {
    return failureCount < 3
  }
  return false
}

const queryCache = new QueryCache({ onError })
const mutationCache = new MutationCache({ onError })

const defaultConfig: QueryClientConfig = {
  queryCache,
  mutationCache,
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: retryFunction,
      retryDelay: calculateRetryDelay,
    },
    mutations: {
      retry: retryFunction,
      retryDelay: calculateRetryDelay,
    },
  },
}

export const getQueryClient_CLIENT_SIDE = () => new QueryClient(defaultConfig)

// Server-side QueryClient — no QueryCache/MutationCache (onError uses window)
export default cache(
  () =>
    new QueryClient({
      defaultOptions: {
        queries: {
          refetchOnWindowFocus: false,
          retry: retryFunction,
          retryDelay: calculateRetryDelay,
          gcTime: 600000, // 10 minutes
        },
        mutations: {
          retry: retryFunction,
          retryDelay: calculateRetryDelay,
          gcTime: 600000, // 10 minutes
        },
      },
    }),
)
